import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  canonicalStepForStatus,
  isFailedStatus,
  PROCESSING_STATUSES,
  type ProcessingStatus,
} from "../../../processing/processingSteps"
import {
  formatInt,
  formatIsoDate,
  localeForLanguage,
} from "../customerDashboardStore"
import type { DashboardWidgetProps } from "../customerWidgetRegistry"
import { requestActionPath } from "./requestActionPath"
import { RequestTopRow } from "./RequestTopRow"

// `state` (get_open_top) is COALESCE(processing_status, status) — either an
// intake status (before the answering pipeline assigns a processing_status)
// or a processing_status itself. Intake statuses have their own dedicated
// labels (reused from InboxView's row-status rendering).
const INTAKE_STATE_LABEL_KEYS: Record<string, TranslationKeys> = {
  extracting: "MC_Q_STATUS_EXTRACTING",
  needs_confirm: "MC_Q_STATUS_NEEDS_CONFIRM",
  stored_pending: "MC_Q_STATUS_STORED_PENDING",
  processed: "MC_Q_STATUS_PROCESSED",
  extraction_failed: "MC_Q_STATUS_EXTRACTION_FAILED",
}

// Every processing_status value canonicalStepForStatus (processingSteps.ts)
// accepts — checked before calling it, since that switch has no default
// branch and is only exhaustive over its literal union; `state` here is a
// plain string, so an unrecognized value must fall back to raw text instead
// of hitting an unhandled case at runtime.
const KNOWN_PROCESSING_STATUSES = new Set<string>(PROCESSING_STATUSES)

// Canonical-step label keys, reused from InboxView's CANONICAL_STEP_LABEL_KEYS
// (QuestionnaireProcessingLayout's step titles).
const CANONICAL_STEP_LABEL_KEYS: Record<
  ReturnType<typeof canonicalStepForStatus>,
  TranslationKeys
> = {
  mapping: "MC_QP_STEP_MAPPING_TITLE",
  reconcile: "MC_QP_STEP_RECONCILE_TITLE",
  gaps: "MC_QP_STEP_GAPS_TITLE",
  review: "MC_QP_STEP_REVIEW_TITLE",
}

function stateLabel(
  state: string,
  translate: (key: TranslationKeys) => string,
): string {
  const intakeKey = INTAKE_STATE_LABEL_KEYS[state]
  if (intakeKey) return translate(intakeKey)
  if (KNOWN_PROCESSING_STATUSES.has(state)) {
    const status = state as ProcessingStatus
    if (isFailedStatus(status)) return translate("MC_Q_PSTATUS_FAILED")
    return translate(CANONICAL_STEP_LABEL_KEYS[canonicalStepForStatus(status)])
  }
  // Genuinely unrecognized state (or a future value not yet mapped) — render
  // raw rather than hide the row's state entirely.
  return state
}

export function OpenWidget({ stats, loadFailed }: DashboardWidgetProps) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  if (stats.openTop.length === 0) {
    // After a failed load the list is unknown, not empty: no "nothing here".
    if (loadFailed) return null
    return (
      <div className="py-6 text-sm text-grey5">
        {translate("MC_SSD_EMPTY_LIST")}
      </div>
    )
  }
  return (
    <div>
      {stats.openTop.map((item) => {
        // The row's actual CTA — routes straight to the row's process step
        // (OpenItem carries no `step`, so review_ready falls back to the bare
        // process path, which auto-redirects to the canonical step — see
        // requestActionPath's docstring).
        const actionTo = requestActionPath(item)
        const overdue = item.overdue
        return (
          <RequestTopRow
            key={item.questionnaireId}
            item={item}
            subtitleTail={
              // Own leaf span (not a bare text sibling of the reference
              // above) so the state text stays independently queryable
              // by its exact label, regardless of whether a reference
              // is also present on the row.
              <span>{stateLabel(item.state, translate)}</span>
            }
            metric={
              // Full-phrase swap on overdue (not an appended suffix) — mirrors
              // the supplier OutstandingWidget's "waiting N days" / "overdue
              // N days" swap.
              <span className={cn(overdue && "text-redLight")}>
                {overdue
                  ? translate("MC_CSD_ROW_OVERDUE_D")
                  : item.dueDate
                    ? translate("MC_CSD_ROW_DUE", {
                        // dueDate is a date-only "YYYY-MM-DD" value — parse it
                        // as local midnight (formatIsoDate), not
                        // `new Date(iso)`'s UTC midnight, which reads one day
                        // early in timezones behind UTC.
                        date: formatIsoDate(item.dueDate, locale),
                      })
                    : "—"}
              </span>
            }
            metricLabel={`${formatInt(item.articles, locale)} ${translate("MC_CSD_ROW_ARTICLES")}`}
            // Always present, unlike the title link — a soft-deleted customer
            // (customerName null) leaves the title as plain, unlinked text,
            // so this is the row's only guaranteed navigation.
            action={{ label: translate("MC_CSD_ROW_VIEW"), to: actionTo }}
          />
        )
      })}
    </div>
  )
}
